import { useCallback } from 'react';
import { useAssetContext } from '../context/AssetContext';
import { uploadAsset, revokeAssetUrl } from '../services/assetService';
import { validateFile } from '../utils/assetValidation';
import type { AssetKey, UploadedAsset } from '../types';

/**
 * Primary hook for asset operations.
 * Wraps AssetContext and coordinates validation + upload + state updates.
 */
export function useAssets() {
  const { state, uploadAsset: storeAsset, setUploading, setUploadError, removeAsset } =
    useAssetContext();

  /**
   * Validates, uploads, and stores an asset.
   * Shows uploading state and handles errors automatically.
   */
  const handleUpload = useCallback(
    async (file: File, key: AssetKey): Promise<UploadedAsset | null> => {
      // Client-side validation first
      const validation = validateFile(file, key);
      if (!validation.valid) {
        setUploadError(key, validation.error ?? 'Invalid file');
        return null;
      }

      setUploading(key);
      try {
        const asset = await uploadAsset(file, key);
        storeAsset(key, asset);
        return asset;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        setUploadError(key, message);
        return null;
      }
    },
    [storeAsset, setUploading, setUploadError]
  );

  /**
   * Removes an asset from state and revokes its object URL (if local).
   */
  const handleRemove = useCallback(
    (key: AssetKey) => {
      const existing = state.assets[key];
      if (existing) revokeAssetUrl(existing);
      removeAsset(key);
    },
    [state.assets, removeAsset]
  );

  return {
    /** All uploaded assets, keyed by AssetKey */
    assets: state.assets,

    /** Per-key uploading state */
    uploading: state.uploading,

    /** Per-key error messages */
    errors: state.errors,

    /** Upload a file for a given slot */
    upload: handleUpload,

    /** Remove an asset from a slot */
    remove: handleRemove,

    /** True if any upload is in progress */
    isAnyUploading: Object.values(state.uploading).some(Boolean),
  };
}
